import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { getStoredUserId } from '../../utils/auth';
import { API_BASE_URL } from '../../constants/apiConstants';
import './FacebookPageAnalytics.css';

const FacebookPageAnalytics = () => {
  const [pages, setPages] = useState([]);
  const [allPages, setAllPages] = useState([]); // Store all pages for filtering
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);

  useEffect(() => {
    fetchPages();
  }, []);

  // Filter pages based on selected Facebook account
  const filterPagesByFbAccount = (fbAccount, allPagesData) => {
    if (!fbAccount || !allPagesData) return [];
    return allPagesData.filter(page => page.facebook_user_id === fbAccount.facebook_user_id);
  };

  // Handle Facebook account change
  const handleFbAccountChange = (account) => {
    setSelectedAccount(account);
    const filteredPages = filterPagesByFbAccount(account, allPages);
    setPages(filteredPages);
    
    // Auto-select first page from filtered list
    if (filteredPages.length > 0) {
      const firstPage = filteredPages[0];
      setSelectedPage(firstPage);
      fetchPageInsights(firstPage.page_id);
    } else {
      setSelectedPage(null);
      setInsights(null);
    }
  };

  const fetchPages = async () => {
    try {
      setLoading(true);
      console.log('🔄 Fetching pages...');
      const response = await axios.get(`${API_BASE_URL}/meta/data`, {
        headers: {
          'userid': localStorage.getItem('userId') || getStoredUserId()
        }
      });

      console.log('📦 Pages response:', response.data);

      if (response.data.success) {
        const allPagesData = response.data.data.facebook_pages || [];
        const token = response.data.data.token || null;
        // Build accounts list from token
        const accounts = token ? [{
          facebook_user_id: token.facebook_user_id,
          facebook_name: token.facebook_name,
          facebook_email: token.facebook_email,
          facebook_picture: token.facebook_picture
        }] : [];
        
        console.log('All pages:', allPagesData.length);
        console.log('Accounts:', accounts.length);
        
        setAllPages(allPagesData);
        setConnectedAccounts(accounts);
        
        // Auto-select first account and show all pages
        if (accounts.length > 0) {
          const firstAccount = accounts[0];
          setSelectedAccount(firstAccount);
          // All pages belong to this user
          setPages(allPagesData);
          
          if (allPagesData.length > 0) {
            const firstPage = allPagesData[0];
            console.log('First page:', firstPage.page_name, 'ID:', firstPage.page_id);
            setSelectedPage(firstPage);
            fetchPageInsights(firstPage.page_id);
          }
        } else {
          setPages([]);
        }
      }
    } catch (err) {
      console.error('Error fetching pages:', err);
      setError('Failed to load Facebook pages');
    } finally {
      setLoading(false);
    }
  };

  const fetchPageInsights = async (pageId) => {
    try {
      setInsightsLoading(true);
      setInsights(null); // Reset insights before fetching
      console.log('🔍 Fetching insights for page:', pageId);
      console.log('Using API URL:', `${API_BASE_URL}/meta/facebook/insights/${pageId}`);
      
      const response = await axios.get(`${API_BASE_URL}/meta/facebook/insights/${pageId}`, {
        headers: {
          'userid': localStorage.getItem('userId') || getStoredUserId()
        }
      });

      console.log('📥 API Response:', response.data);
      console.log('Response success?', response.data.success);
      console.log('Response data?', response.data.data);
      
      if (response.data.success && response.data.data) {
        console.log('📊 Setting insights state...');
        console.log('Insights data:', response.data.data);
        console.log('Fan count:', response.data.data.fan_count);
        console.log('Followers count:', response.data.data.followers_count);
        console.log('Insights array:', response.data.data.insights);
        console.log('Insights type:', typeof response.data.data.insights);
        console.log('Is array?', Array.isArray(response.data.data.insights));
        setInsights(response.data.data);
        console.log('✅ Insights state updated');
      } else {
        console.warn('❌ API returned success: false or no data');
        console.warn('Full response:', JSON.stringify(response.data, null, 2));
      }
    } catch (err) {
      console.error('❌ Error fetching insights:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
    } finally {
      setInsightsLoading(false);
    }
  };

  const handlePageSelect = (page) => {
    setSelectedPage(page);
    fetchPageInsights(page.page_id);
  };

  const handleDisconnect = async () => {
    const accountName = selectedAccount?.facebook_name || 'this Facebook account';
    const multipleAccounts = connectedAccounts.length > 1;
    
    const message = multipleAccounts
      ? `Disconnect ${accountName}? You have ${connectedAccounts.length} accounts connected.`
      : 'Are you sure you want to disconnect your Facebook account? This will remove all connected pages and data.';
    
    if (!window.confirm(message)) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE_URL}/meta/auth/disconnect`, {}, {
        headers: {
          'userid': localStorage.getItem('userId') || getStoredUserId()
        }
      });

      if (response.data.success) {
        alert('Facebook account disconnected successfully');
        // Refresh data to get updated account list
        await fetchPages();
      }
    } catch (err) {
      console.error('Error disconnecting:', err);
      alert('Failed to disconnect account: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshInsights = async () => {
    if (!selectedPage) return;
    await fetchPageInsights(selectedPage.page_id);
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  // Extract historical values from insights for a specific metric
  const getInsightHistory = (metricName) => {
    if (!insights?.insights || !Array.isArray(insights.insights)) return [];
    const metric = insights.insights.find(m => m.name === metricName);
    if (!metric?.values || !Array.isArray(metric.values)) return [];
    return metric.values.map(v => ({
      value: v.value || 0,
      date: v.end_time ? new Date(v.end_time) : null
    })).filter(v => v.date);
  };

  // Generate historical trend data from actual insights
  const generateTrendData = () => {
    // Get historical data for available metrics
    const impressionsUniqueHistory = getInsightHistory('page_impressions_unique');
    const pageViewsHistory = getInsightHistory('page_views_total');

    console.log('📈 Generating trend data...');
    console.log('Unique impressions history length:', impressionsUniqueHistory.length);
    console.log('Page views history length:', pageViewsHistory.length);
    console.log('Has insights?', !!insights?.insights);

    // If we have historical data, use it
    if (impressionsUniqueHistory.length > 0 || pageViewsHistory.length > 0) {
      // Use whichever has more data points as the base
      const baseHistory = impressionsUniqueHistory.length >= pageViewsHistory.length 
        ? impressionsUniqueHistory 
        : pageViewsHistory;
      
      // Build trend data from actual values
      const trendData = baseHistory.map((item, index) => {
        const dateStr = item.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        
        return {
          date: dateStr,
          uniqueImpressions: impressionsUniqueHistory[index]?.value || 0,
          pageViews: pageViewsHistory[index]?.value || 0,
          fullDate: item.date
        };
      });

      // Sort by date (oldest to newest)
      trendData.sort((a, b) => a.fullDate - b.fullDate);
      console.log('✅ Generated trend data from history:', trendData.length, 'points');
      return trendData;
    }

    // If no historical data but we have current insight values, create a single data point for today
    if (insights?.insights) {
      const currentUniqueImpressions = getInsightValue('page_impressions_unique');
      const currentPageViews = getInsightValue('page_views_total');
      
      console.log('Current values - Unique Impressions:', currentUniqueImpressions, 'Page Views:', currentPageViews);
      
      // Only create a data point if we have at least some non-zero values
      if (currentUniqueImpressions > 0 || currentPageViews > 0) {
        const today = new Date();
        const todayData = [{
          date: today.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          uniqueImpressions: currentUniqueImpressions,
          pageViews: currentPageViews,
          fullDate: today
        }];
        console.log('✅ Generated single data point for today:', todayData);
        return todayData;
      }
    }

    // No real data available
    console.log('⚠️ No trend data available');
    return [];
  };

  const getInsightValue = (metricName) => {
    if (!insights?.insights) return 0;
    const metric = insights.insights.find(m => m.name === metricName);
    if (!metric?.values?.length) return 0;
    return metric.values[metric.values.length - 1]?.value || 0;
  };

  if (loading) {
    return (
      <div className="facebook-analytics-loading">
        <div className="spinner"></div>
        <p>Loading Facebook pages...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="facebook-analytics-error">
        <p>{error}</p>
        <button onClick={fetchPages}>Retry</button>
      </div>
    );
  }

  if (pages.length === 0) {
    // If a specific account is selected but has no pages
    if (selectedAccount) {
      return (
        <div className="facebook-analytics-empty">
          <p>No Facebook Pages for {selectedAccount.facebook_name}</p>
          <p className="hint">This Facebook account doesn't have any pages connected yet</p>
          {connectedAccounts.length > 1 && (
            <p className="hint">Try switching to a different account using the dropdown above</p>
          )}
        </div>
      );
    }
    
    // No accounts at all
    return (
      <div className="facebook-analytics-empty">
        <p>No Facebook pages connected</p>
        <p className="hint">Connect your Facebook account to see page analytics</p>
      </div>
    );
  }

  return (
    <div className="facebook-analytics">
      <div className="analytics-header">
        <div className="header-top">
          <h2>Facebook Page Analytics</h2>
          <div className="header-actions">
            {connectedAccounts.length > 0 && (
              <>
                {connectedAccounts.length > 1 ? (
                  <div className="account-switcher">
                    <label className="account-switcher-label">Connected Account:</label>
                    <select 
                      className="account-select"
                      value={selectedAccount?.facebook_user_id || ''}
                      onChange={(e) => {
                        const account = connectedAccounts.find(acc => acc.facebook_user_id === e.target.value);
                        handleFbAccountChange(account);
                      }}
                    >
                      {connectedAccounts.map((account) => (
                        <option key={account.facebook_user_id} value={account.facebook_user_id}>
                          {account.facebook_name} ({account.facebook_email})
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="connected-account-badge">
                    <img 
                      src={selectedAccount?.facebook_picture} 
                      alt={selectedAccount?.facebook_name} 
                      className="connected-avatar"
                    />
                    <div className="connected-info">
                      <span className="connected-label">Connected as</span>
                      <span className="connected-name">{selectedAccount?.facebook_name}</span>
                    </div>
                  </div>
                )}
                <button 
                  className="action-btn refresh-btn" 
                  onClick={handleRefreshInsights}
                  disabled={insightsLoading || !selectedPage}
                  title="Refresh insights from Facebook"
                >
                  {insightsLoading ? '🔄' : '↻'} Refresh
                </button>
                <button 
                  className="action-btn disconnect-btn" 
                  onClick={handleDisconnect}
                  title="Disconnect Facebook account"
                >
                  ✕ Disconnect
                </button>
              </>
            )}
          </div>
        </div>
        <div className="page-selector">
          {pages.map((page) => (
            <button
              key={page.page_id}
              className={`page-tab ${selectedPage?.page_id === page.page_id ? 'active' : ''}`}
              onClick={() => handlePageSelect(page)}
            >
              {page.page_name}
            </button>
          ))}
        </div>
      </div>

      {selectedPage && (
        <div className="analytics-content">
          {/* Page Overview */}
          <div className="page-overview">
            <div className="page-header">
              {selectedPage.picture_url && (
                <img src={selectedPage.picture_url} alt={selectedPage.page_name} className="page-avatar" />
              )}
              <div className="page-info">
                <h3>{selectedPage.page_name}</h3>
                <p className="page-category">{selectedPage.page_category}</p>
              </div>
            </div>
          </div>

          {/* Key Metrics */}
          {insightsLoading ? (
            <div className="insights-loading">
              <div className="spinner-small"></div>
              <p>Loading insights...</p>
            </div>
          ) : (
            <>
              <div className="metrics-grid">
                <div className="metric-card">
                  <div className="metric-icon">👥</div>
                  <div className="metric-content">
                    <p className="metric-label">Total Fans</p>
                    <h3 className="metric-value">{formatNumber(insights?.fan_count || selectedPage.fan_count || 0)}</h3>
                  </div>
                </div>

                <div className="metric-card">
                  <div className="metric-icon">🎯</div>
                  <div className="metric-content">
                    <p className="metric-label">Unique Reach</p>
                    <h3 className="metric-value">{formatNumber(getInsightValue('page_impressions_unique'))}</h3>
                    <p className="metric-subtitle">Unique viewers</p>
                  </div>
                </div>

                <div className="metric-card">
                  <div className="metric-icon">📊</div>
                  <div className="metric-content">
                    <p className="metric-label">Page Views</p>
                    <h3 className="metric-value">{formatNumber(getInsightValue('page_views_total'))}</h3>
                    <p className="metric-subtitle">Total views</p>
                  </div>
                </div>

                <div className="metric-card">
                  <div className="metric-icon">👤</div>
                  <div className="metric-content">
                    <p className="metric-label">Followers</p>
                    <h3 className="metric-value">{formatNumber(insights?.followers_count || selectedPage.followers_count || 0)}</h3>
                  </div>
                </div>
              </div>

              {/* Historical Trends */}
              <div className="trends-section">
                <h4>Performance Trends</h4>
                <p className="trends-subtitle">
                  {generateTrendData().length > 0 
                    ? `Showing ${generateTrendData().length} day${generateTrendData().length !== 1 ? 's' : ''} of historical data from Facebook`
                    : 'No historical data available yet'}
                </p>
                
                {generateTrendData().length > 0 ? (
                  <div className="charts-grid">
                    {/* Page Views Chart */}
                    <div className="chart-card">
                      <h5>Page Views Trend</h5>
                      <ResponsiveContainer width="100%" height={250}>
                        <AreaChart data={generateTrendData()}>
                          <defs>
                            <linearGradient id="colorPageViews" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                              <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="date" stroke="#6b7280" style={{ fontSize: '12px' }} />
                          <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'white', 
                              border: '1px solid #e5e7eb', 
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                            }} 
                          />
                          <Area type="monotone" dataKey="pageViews" stroke="#10b981" fillOpacity={1} fill="url(#colorPageViews)" name="Page Views" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Unique Reach Chart */}
                    <div className="chart-card">
                      <h5>Unique Reach Trend</h5>
                      <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={generateTrendData()}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="date" stroke="#6b7280" style={{ fontSize: '12px' }} />
                          <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'white', 
                              border: '1px solid #e5e7eb', 
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                            }} 
                          />
                          <Line type="monotone" dataKey="uniqueImpressions" stroke="#1877F2" strokeWidth={2} dot={{ fill: '#1877F2', r: 4 }} name="Unique Reach" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ) : (
                  <div className="no-data-message">
                    <p>📊 No historical trend data available</p>
                    <p className="no-data-subtitle">
                      {selectedPage?.fan_count >= 100 
                        ? 'Click "Refresh Insights" above to fetch the last 7 days of data from Facebook.'
                        : 'This page needs 100+ followers before Facebook provides insights data.'
                      }
                    </p>
                  </div>
                )}
              </div>

              {/* Page Details */}
              <div className="page-details">
                <h4>Page Information</h4>
                <div className="details-grid">
                  {selectedPage.about && (
                    <div className="detail-item">
                      <strong>About:</strong>
                      <p>{selectedPage.about}</p>
                    </div>
                  )}
                  {selectedPage.website && (
                    <div className="detail-item">
                      <strong>Website:</strong>
                      <a href={selectedPage.website} target="_blank" rel="noopener noreferrer">
                        {selectedPage.website}
                      </a>
                    </div>
                  )}
                  {selectedPage.phone && (
                    <div className="detail-item">
                      <strong>Phone:</strong>
                      <p>{selectedPage.phone}</p>
                    </div>
                  )}
                  {selectedPage.page_tasks && selectedPage.page_tasks.length > 0 && (
                    <div className="detail-item">
                      <strong>Permissions:</strong>
                      <div className="tasks-list">
                        {selectedPage.page_tasks.map((task, idx) => (
                          <span key={idx} className="task-badge">{task}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {insights?.cached && (
                <p className="cached-notice">
                  ℹ️ Showing cached insights. Data is refreshed during authentication.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FacebookPageAnalytics;
